/**
 * Счётчик открытых задач «на сегодня» для деска Google Ads в меню кабинета.
 * Канонический источник — «Задачи 2.0» (в т.ч. суррогаты gcal:…); легаси-доска в UI не учитывается.
 */

export function localCalendarYmd(d: Date = new Date()): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function parseListDateYmd(name: unknown): string | null {
  const n = String(name ?? '').trim()
  if (!/^\d{4}-\d{2}-\d{2}/.test(n)) return null
  return n.slice(0, 10)
}

function dueYmdFromTb2(task: { due_at?: string | null }): string | null {
  const s = (task.due_at && String(task.due_at).trim()) || ''
  if (s.length >= 10 && s[4] === '-' && s[7] === '-') return s.slice(0, 10)
  return null
}

function tb2Open(task: { status?: string }): boolean {
  const st = String(task.status || '').toLowerCase()
  return st !== 'done' && st !== 'archived'
}

function cardOpen(c: { done?: boolean; status?: string }): boolean {
  if (c.done) return false
  return String(c.status || '').toLowerCase() !== 'done'
}

export type BoardListCard = { id?: string; done?: boolean; status?: string }
export type BoardList = { name?: string; cards?: BoardListCard[] }
export type BoardPayload = { lists?: BoardList[]; google_ads_calendar_board_id?: string }

export type Tb2TaskMin = {
  id?: string
  status?: string
  due_at?: string | null
  title?: string
  description?: string
  client_id?: string | null
  client_name?: string | null
  created_at?: string
}

export const GOOGLE_DESK_TASKS_CHANGED_EVENT = 'google-desk-tasks-changed'

function sortGoogleDeskTasks(a: Tb2TaskMin, b: Tb2TaskMin): number {
  const da = dueYmdFromTb2(a) || ''
  const db = dueYmdFromTb2(b) || ''
  if (da !== db) return da.localeCompare(db)
  const ca = String(a.created_at || '')
  const cb = String(b.created_at || '')
  if (ca !== cb) return ca.localeCompare(cb)
  return String(a.title || '').localeCompare(String(b.title || ''))
}

/** Открытые задачи с датой ровно todayYmd (без просроченных с прошлых дней). */
export function filterOpenTasksDueTodayForGoogleDesk(
  tb2Tasks: Tb2TaskMin[] | null | undefined,
  todayYmd: string,
): Tb2TaskMin[] {
  const out: Tb2TaskMin[] = []
  for (const task of tb2Tasks || []) {
    if (!tb2Open(task)) continue
    const due = dueYmdFromTb2(task)
    if (due !== todayYmd) continue
    out.push(task)
  }
  out.sort(sortGoogleDeskTasks)
  return out
}

/** Открытые задачи с датой ровно todayYmd (без просроченных с прошлых дней). */
export function countOpenTasksDueTodayForGoogleDesk(
  board: BoardPayload | null | undefined,
  tb2Tasks: Tb2TaskMin[] | null | undefined,
  todayYmd: string,
): number {
  const lists = board?.lists || []
  const seenCal = new Set<string>()
  let n = 0
  for (const list of lists) {
    const d = parseListDateYmd(list.name)
    if (d !== todayYmd) continue
    for (const c of list.cards || []) {
      if (!cardOpen(c)) continue
      const cid = String(c.id || '').trim()
      if (cid) seenCal.add(cid)
      n += 1
    }
  }
  for (const task of tb2Tasks || []) {
    if (!tb2Open(task)) continue
    const due = dueYmdFromTb2(task)
    if (due !== todayYmd) continue
    const tid = String(task.id || '')
    if (tid.startsWith('gcal:')) {
      const raw = tid.slice(5).trim()
      if (raw && seenCal.has(raw)) continue
    }
    n += 1
  }
  return n
}
